"use client"

import { moneyLib } from "@agentcall/core/lib"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { tenantApi } from "@/api/client/tenant"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomHeader } from "@/components/properties/RoomHeader"
import {
  TenantForm,
  type TenantFormDefaultValues,
  type TenantFormValues,
} from "@/components/properties/TenantForm"
import { Button } from "@/components/ui/button/button"
import { Loading } from "@/components/ui/loading"
import { toast } from "@/hooks/use-toast"
import { toOccupantsFormValues, toOccupantsPayload } from "@/pages/new-tenant-register/components"

export function TenantEditPage() {
  const navigate = useNavigate()
  const { propertyId: propertyIdParam, roomId: roomIdParam, tenantId: tenantIdParam } = useParams()
  const parsedPropertyId = Number(propertyIdParam)
  const propertyId = Number.isFinite(parsedPropertyId) ? parsedPropertyId : undefined
  const parsedRoomId = Number(roomIdParam)
  const roomId = Number.isFinite(parsedRoomId) ? parsedRoomId : undefined
  const parsedTenantId = Number(tenantIdParam)
  const tenantId = Number.isFinite(parsedTenantId) ? parsedTenantId : undefined

  const [isLoading, setIsLoading] = useState(true)
  const [tenantData, setTenantData] = useState<TenantFormDefaultValues | null>(null)
  const hasValidIds = propertyId !== undefined && roomId !== undefined && tenantId !== undefined

  const {
    data: tenantResponse,
    isLoading: isTenantLoading,
    error: tenantError,
    refetch,
  } = tenantApi.useTenantGet(
    tenantId,
    { withOccupants: "true" },
    {
      query: {
        enabled: tenantId !== undefined,
      },
    }
  )

  const tenantUpdateMutation = tenantApi.useTenantUpdate({
    mutation: {
      onSuccess: async () => {
        toast({
          title: "更新が完了しました",
        })
        await refetch()
        navigateToRoom()
      },
    },
  })

  const navigateToRoom = () => {
    if (propertyId !== undefined && roomId !== undefined) {
      navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { shouldRefetch: true } })
      return
    }

    if (propertyId !== undefined) {
      navigate(`/properties/${propertyId}`)
      return
    }

    navigate("/properties")
  }

  useEffect(() => {
    if (tenantResponse?.tenant) {
      const tenant = tenantResponse.tenant as typeof tenantResponse.tenant & {
        phone4?: string
        phone5?: string
        phone6?: string
      }
      setTenantData({
        fullName: tenant.name ?? "",
        fullNameKana: tenant.nameKana ?? "",
        phone1: tenant.phone1 ?? "",
        phone2: tenant.phone2 ?? "",
        phone3: tenant.phone3 ?? "",
        phone4: tenant.phone4 ?? "",
        phone5: tenant.phone5 ?? "",
        phone6: tenant.phone6 ?? "",
        email: tenant.email ?? "",
        purpose: tenant.purpose ?? "",
        startDate: tenant.startDate ? new Date(tenant.startDate) : undefined,
        rent: tenant.rent !== undefined && tenant.rent !== null ? String(tenant.rent) : "",
        maintenanceFee:
          tenant.commonAreaFee !== undefined && tenant.commonAreaFee !== null
            ? String(tenant.commonAreaFee)
            : "",
        hasParking: tenant.isParking ?? false,
        parkingLotNumber: tenant.parkingNumber ?? "",
        parkingFee:
          tenant.parkingFee !== undefined && tenant.parkingFee !== null
            ? String(moneyLib.deriveBaseFee(tenant.parkingFee))
            : "",
        otherFees:
          tenant.otherFees && tenant.otherFees.length > 0
            ? tenant.otherFees.map((fee) => ({
                name: fee.label ?? "",
                baseFee: fee.price !== undefined && fee.price !== null ? String(fee.price) : "",
                tax: fee.tax !== undefined && fee.tax !== null ? String(fee.tax) : "",
              }))
            : [{ name: "", baseFee: "", tax: "" }],
        occupants: toOccupantsFormValues(tenant.occupants),
      })
      setIsLoading(false)
    } else if (!isTenantLoading) {
      setIsLoading(false)
    }
  }, [tenantResponse, isTenantLoading])

  const handleSubmit = async (values: TenantFormValues) => {
    if (tenantId === undefined || roomId === undefined) {
      navigateToRoom()
      return
    }

    try {
      const toNumber = (value?: string) => {
        const parsed = Number(value ?? 0)
        return Number.isFinite(parsed) ? parsed : 0
      }

      // 未入力の行は送らない（新規入居者登録フォームと同じ扱い）
      const otherFees = values.otherFees
        .filter((fee) => fee.name && fee.baseFee)
        .map((fee) => ({
          label: fee.name,
          price: toNumber(fee.baseFee),
          tax: toNumber(fee.tax),
        }))

      // 駐車場代は税込みで保存（税抜き金額 + 税額）
      const parkingBaseFee = toNumber(values.parkingFee)
      const parkingFeeWithTax = parkingBaseFee + moneyLib.calculateTax(parkingBaseFee)

      const data = {
        roomId,
        name: values.fullName,
        nameKana: values.fullNameKana,
        phone1: values.phone1,
        phone2: values.phone2,
        phone3: values.phone3,
        phone4: values.phone4,
        phone5: values.phone5,
        phone6: values.phone6,
        email: values.email ?? "",
        // 生年月日はこのフォームでは編集しないため、登録済みの値を保つ
        birthday: tenantResponse?.tenant?.birthday ?? null,
        startDate: values.startDate,
        rent: toNumber(values.rent),
        commonAreaFee: toNumber(values.maintenanceFee),
        isParking: values.hasParking,
        parkingFee: parkingFeeWithTax,
        parkingNumber: values.parkingLotNumber ?? "",
        otherFees,
        purpose: values.purpose,
        occupants: toOccupantsPayload(values.occupants),
      }

      await tenantUpdateMutation.mutateAsync({
        id: String(tenantId),
        data,
      })
      navigateToRoom()
    } catch (error) {
      console.error("Error updating tenant:", error)
    }
  }

  if (isLoading) {
    return <Loading className="mt-10" text="賃借人を読み込んでいます..." />
  }

  if (!hasValidIds || tenantError || !tenantData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <div className="text-slate-600">
          {hasValidIds ? "賃借人の取得に失敗しました。" : "賃借人が見つかりませんでした。"}
        </div>
        <Button onClick={() => navigate(-1)} variant="outline">
          前のページに戻る
        </Button>
      </div>
    )
  }

  if (!propertyId || !roomId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDまたは部屋IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />
      <RoomHeader roomId={roomId} />

      <TenantForm
        defaultValues={tenantData}
        onSubmit={handleSubmit}
        onCancel={navigateToRoom}
        isEdit
        isSubmitting={tenantUpdateMutation.isPending}
      />
    </div>
  )
}
